import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import UploadOutlined from "@mui/icons-material/UploadOutlined";
import SearchOutlined from "@mui/icons-material/SearchOutlined";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import VisibilityOutlined from "@mui/icons-material/VisibilityOutlined";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import { ONTOLOGY_WORKBENCH_STEPS } from "../../data/create-ontology-questions.data";
import {
  DUMMY_UPLOADED_DOCUMENTS,
  GROUND_TO_DOCUMENTS_KPI,
  PROCESSING_FILE,
  RELATIONSHIP_VALIDATION_SUMMARY,
  TOTAL_UPLOADED_DOCUMENTS_COUNT,
  DocumentStatus
} from "../../data/create-ontology-ground-to-documents.data";
import "./CreateOntologyGroundToDocuments.scss";

const STATUS_CLASS: Record<DocumentStatus, string> = {
  Grounded: "create-ground-doc-status--grounded",
  Processing: "create-ground-doc-status--processing"
};

const CreateOntologyGroundToDocuments: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const initiativeId = (location.state as { initiativeId?: string } | null)
    ?.initiativeId;

  const handleBack = () => {
    navigate("/ontology-studio/create/reuse-harmonisation", {
      state: { initiativeId }
    });
  };

  // Next step (Build Knowledge Graph) design not provided yet — return to the dashboard for now.
  const handleContinue = () => {
    navigate("/create-ontology");
  };

  const handleCreateGraph = () => {
    navigate("/ontology-studio/create/build-knowledge-graph", {
      state: { initiativeId }
    });
  };

  return (
    <div className="create-ground-doc-container">
      <nav className="create-ground-doc-breadcrumb">
        <Link to="/home" className="breadcrumb-link">
          Engineering Information Management
        </Link>
        <span className="breadcrumb-separator">&gt;</span>
        <Link to="/create-ontology" className="breadcrumb-link">
          Ontology Studio
        </Link>
        <span className="breadcrumb-separator">&gt;</span>
        <span className="breadcrumb-current">Ground to Documents</span>
      </nav>

      <div className="create-ground-doc-header">
        <h1 className="create-ground-doc-title">Ground to Documents</h1>
        <p className="create-ground-doc-description">
          Upload source evidence, ground concepts and relationships, validate
          mappings, resolve unmatched content, and confirm provenance before the
          knowledge graph is built.
        </p>
        <div className="create-ground-doc-meta">
          <span className="create-ground-doc-meta__pill">
            Initiative ID <strong>{initiativeId ?? "EY-ONT-2026-TEMP"}</strong>
          </span>
          <span className="create-ground-doc-meta__pill">
            Status <strong>Draft</strong>
          </span>
        </div>
      </div>

      <div className="create-ground-doc-workflow">
        <div className="create-ground-doc-workflow__row">
          {ONTOLOGY_WORKBENCH_STEPS.slice(0, 3).map((step) => (
            <div
              key={step.id}
              className="create-ground-doc-workflow__step create-ground-doc-workflow__step--done"
            >
              <span className="create-ground-doc-workflow__indicator">✓</span>
              <span className="create-ground-doc-workflow__label">
                {step.label}
              </span>
            </div>
          ))}
        </div>
        <div className="create-ground-doc-workflow__row">
          {ONTOLOGY_WORKBENCH_STEPS.slice(3, 6).map((step) => (
            <div
              key={step.id}
              className={`create-ground-doc-workflow__step${
                step.id === 5 ? " create-ground-doc-workflow__step--active" : ""
              }${step.id < 5 ? " create-ground-doc-workflow__step--done" : ""}`}
            >
              <span className="create-ground-doc-workflow__indicator">
                {step.id < 5 ? "✓" : step.id === 5 ? "•" : ""}
              </span>
              <span className="create-ground-doc-workflow__label">
                {step.label}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="create-ground-doc-kpi-grid">
        <div className="create-ground-doc-kpi-card">
          <span className="create-ground-doc-kpi-card__label">
            Documents Uploaded
          </span>
          <span className="create-ground-doc-kpi-card__value">
            {GROUND_TO_DOCUMENTS_KPI.documentsUploaded}
          </span>
          <span className="create-ground-doc-kpi-card__caption">
            Total evidence files
          </span>
        </div>
        <div className="create-ground-doc-kpi-card">
          <span className="create-ground-doc-kpi-card__label">
            Concepts Grounded
          </span>
          <span className="create-ground-doc-kpi-card__value">
            {GROUND_TO_DOCUMENTS_KPI.conceptsGrounded}
          </span>
          <span className="create-ground-doc-kpi-card__caption">
            Entities linked to source evidence
          </span>
        </div>
        <div className="create-ground-doc-kpi-card">
          <span className="create-ground-doc-kpi-card__label">
            Relationships Extracted
          </span>
          <span className="create-ground-doc-kpi-card__value">
            {GROUND_TO_DOCUMENTS_KPI.relationshipsExtracted}
          </span>
          <span className="create-ground-doc-kpi-card__caption">
            Automatically inferred from uploaded sources
          </span>
        </div>
        <div className="create-ground-doc-kpi-card">
          <span className="create-ground-doc-kpi-card__label">Coverage %</span>
          <span className="create-ground-doc-kpi-card__value">
            {GROUND_TO_DOCUMENTS_KPI.coveragePercent}%
          </span>
          <span className="create-ground-doc-kpi-card__caption">
            Evidence coverage across uploaded sources
          </span>
        </div>
      </div>

      <div className="create-ground-doc-content">
        <div className="create-ground-doc-left-panel">
          <div className="create-ground-doc-upload-card">
            <div className="create-ground-doc-upload-card__header">
              <p className="create-ground-doc-upload-card__title">
                Upload Source Evidence
              </p>
              <p className="create-ground-doc-upload-card__description">
                Upload PDF, DOCX, XLSX, CSV, asset registers, and engineering
                documentation to review source evidence and support entity
                decisions.
              </p>
            </div>
            <div className="create-ground-doc-dropzone">
              <span className="create-ground-doc-dropzone__icon-ring">
                <UploadOutlined fontSize="small" />
              </span>
              <p className="create-ground-doc-dropzone__title">
                Drag &amp; drop source files here
              </p>
              <p className="create-ground-doc-dropzone__subtitle">
                PDF, DOCX, XLSX, CSV, asset registers, and engineering
                documentation
              </p>
              <button type="button" className="create-ground-doc-browse-btn">
                Browse files
              </button>
            </div>
            <div className="create-ground-doc-progress">
              <div className="create-ground-doc-progress__row">
                <span>Processing: {PROCESSING_FILE.name}</span>
                <span>{PROCESSING_FILE.progressPercent}%</span>
              </div>
              <div className="create-ground-doc-progress__track">
                <div
                  className="create-ground-doc-progress__fill"
                  style={{ width: `${PROCESSING_FILE.progressPercent}%` }}
                />
              </div>
            </div>
          </div>

          <div className="create-ground-doc-sources-card">
            <div className="create-ground-doc-sources-card__header">
              <div className="create-ground-doc-sources-card__heading">
                <div className="create-ground-doc-sources-card__title-row">
                  <p className="create-ground-doc-sources-card__title">
                    Uploaded Sources
                  </p>
                  <span className="create-ground-doc-sources-card__count">
                    {TOTAL_UPLOADED_DOCUMENTS_COUNT} files
                  </span>
                </div>
                <p className="create-ground-doc-sources-card__subtitle">
                  {DUMMY_UPLOADED_DOCUMENTS.length} of{" "}
                  {TOTAL_UPLOADED_DOCUMENTS_COUNT} shown · Source documents for
                  this initiative
                </p>
              </div>
              <div className="create-ground-doc-filter">
                <SearchOutlined fontSize="small" />
                <span>Filter documents...</span>
              </div>
            </div>

            <table className="create-ground-doc-table">
              <thead>
                <tr>
                  <th className="create-ground-doc-table__col-name">
                    Document Name
                  </th>
                  <th className="create-ground-doc-table__col-type">Type</th>
                  <th className="create-ground-doc-table__col-status">
                    Status
                  </th>
                  <th className="create-ground-doc-table__col-actions">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {DUMMY_UPLOADED_DOCUMENTS.map((doc) => (
                  <tr key={doc.id}>
                    <td className="create-ground-doc-table__col-name">
                      <DescriptionOutlined fontSize="inherit" />
                      <span>{doc.name}</span>
                    </td>
                    <td className="create-ground-doc-table__col-type">
                      {doc.type}
                    </td>
                    <td className="create-ground-doc-table__col-status">
                      <span
                        className={`create-ground-doc-status ${
                          STATUS_CLASS[doc.status]
                        }`}
                      >
                        {doc.status}
                      </span>
                    </td>
                    <td className="create-ground-doc-table__col-actions">
                      <button
                        type="button"
                        className="create-ground-doc-icon-btn"
                        aria-label={`View ${doc.name}`}
                      >
                        <VisibilityOutlined fontSize="inherit" />
                      </button>
                      <button
                        type="button"
                        className="create-ground-doc-icon-btn create-ground-doc-icon-btn--danger"
                        aria-label={`Delete ${doc.name}`}
                      >
                        <DeleteOutline fontSize="inherit" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="create-ground-doc-right-panel">
          <div className="create-ground-doc-validation-card">
            <p className="create-ground-doc-validation-card__title">
              Relationship Validation
            </p>
            <p className="create-ground-doc-validation-card__description">
              Review and validate relationships automatically extracted from
              uploaded source evidence.
            </p>
            <div className="create-ground-doc-stat-list">
              <div className="create-ground-doc-stat">
                <span className="create-ground-doc-stat__label">
                  Total Relationships Extracted
                </span>
                <span className="create-ground-doc-stat__value">
                  {RELATIONSHIP_VALIDATION_SUMMARY.totalExtracted}
                </span>
              </div>
              <div className="create-ground-doc-stat">
                <span className="create-ground-doc-stat__label">
                  Pending Validation
                </span>
                <span className="create-ground-doc-stat__value">
                  {RELATIONSHIP_VALIDATION_SUMMARY.pendingValidation}
                </span>
              </div>
              <div className="create-ground-doc-stat">
                <span className="create-ground-doc-stat__label">Validated</span>
                <span className="create-ground-doc-stat__value">
                  {RELATIONSHIP_VALIDATION_SUMMARY.validated}
                </span>
              </div>
              <div className="create-ground-doc-stat">
                <span className="create-ground-doc-stat__label">
                  AI Confidence
                </span>
                <span className="create-ground-doc-stat__value">
                  {RELATIONSHIP_VALIDATION_SUMMARY.aiConfidencePercent}%
                </span>
              </div>
            </div>
          </div>

          <div className="create-ground-doc-summary-card">
            <p className="create-ground-doc-summary-card__title">
              Grounding Summary
            </p>
            <div className="create-ground-doc-stat-list">
              <div className="create-ground-doc-stat">
                <span className="create-ground-doc-stat__label">
                  Documents Uploaded
                </span>
                <span className="create-ground-doc-stat__value">
                  {GROUND_TO_DOCUMENTS_KPI.documentsUploaded}
                </span>
              </div>
              <div className="create-ground-doc-stat">
                <span className="create-ground-doc-stat__label">
                  Concepts Grounded
                </span>
                <span className="create-ground-doc-stat__value">
                  {GROUND_TO_DOCUMENTS_KPI.conceptsGrounded}
                </span>
              </div>
              <div className="create-ground-doc-stat">
                <span className="create-ground-doc-stat__label">
                  Relationships Extracted
                </span>
                <span className="create-ground-doc-stat__value">
                  {GROUND_TO_DOCUMENTS_KPI.relationshipsExtracted}
                </span>
              </div>
              <div className="create-ground-doc-stat">
                <span className="create-ground-doc-stat__label">
                  Coverage %
                </span>
                <span className="create-ground-doc-stat__value">
                  {GROUND_TO_DOCUMENTS_KPI.coveragePercent}%
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="create-ground-doc-footer">
        <button
          type="button"
          className="create-ground-doc-btn create-ground-doc-btn--secondary"
          onClick={handleBack}
        >
          Back
        </button>
        <div className="create-ground-doc-footer__right">
          <button
            type="button"
            className="create-ground-doc-btn create-ground-doc-btn--secondary"
            onClick={handleContinue}
          >
            Proceed to Relationship Validation
          </button>
          <button
            type="button"
            className="create-ground-doc-btn create-ground-doc-btn--primary"
            onClick={handleCreateGraph}
          >
            Skip &amp; Create Graph &amp; Continue →
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateOntologyGroundToDocuments;
